import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import PageHeader from '@/components/feature/PageHeader';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import FilterBar from './components/FilterBar';
import { articles } from '@/mocks/articles';
import { categories, site } from '@/mocks/site';
import { useSeo } from '@/hooks/useSeo';

const heatValue = (heat: string) => parseFloat(heat.replace('万', '')) || 0;

export default function Collections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState('latest');

  const activeCat = searchParams.get('cat') || 'all';
  const keyword = searchParams.get('q') || '';

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeCat, keyword, sort]);

  useSeo({
    title: '爆料合集库 - 网曝黑料合集帖分类整理 | 瓜田档案局',
    description:
      '按娱乐圈、网红圈、商界、电竞圈等主题分类浏览网曝黑料合集帖，支持关键词搜索与热度、时间节点排序，快速找到你要的那份档案。',
    keywords: '黑料合集,爆料合集,网曝黑料分类,黑料时间线,合集整理',
    path: '/collections',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '爆料合集库',
      description: '按圈子分类整理的网曝黑料合集帖列表。',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hychesuan.cn/' },
          { '@type': 'ListItem', position: 2, name: '爆料合集', item: 'https://hychesuan.cn/collections' },
        ],
      },
    },
  });

  const filtered = useMemo(() => {
    const list = articles.filter((item) => {
      const matchCat = activeCat === 'all' || item.category === activeCat;
      const q = keyword.trim().toLowerCase();
      const matchKeyword =
        !q ||
        item.title.toLowerCase().includes(q) ||
        item.excerpt.toLowerCase().includes(q) ||
        item.categoryName.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCat && matchKeyword;
    });

    return [...list].sort((a, b) => {
      if (sort === 'hot') return heatValue(b.heat) - heatValue(a.heat);
      if (sort === 'nodes') return b.timeline.length - a.timeline.length;
      return b.date.localeCompare(a.date);
    });
  }, [activeCat, keyword, sort]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <PageHeader
          eyebrow="爆料合集库"
          title="网曝黑料合集帖分类整理"
          desc="截止目前共归档 392 起事件的合集帖。按圈子筛选、按关键词检索，每份档案都附完整时间线与素材清单。"
        />

        <div className="mx-auto max-w-content px-4 md:px-6 py-10 md:py-14">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: '爆料合集' },
            ]}
          />

          <FilterBar
            activeCat={activeCat}
            keyword={keyword}
            sort={sort}
            onChangeCat={(id) => updateParam('cat', id === 'all' ? '' : id)}
            onChangeKeyword={(value) => updateParam('q', value)}
            onChangeSort={setSort}
          />

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
            <span>
              共找到 <strong className="font-heading text-foreground-950">{filtered.length}</strong> 份档案
            </span>
            {activeCat !== 'all' && (
              <span className="rounded-full bg-secondary-100 px-3 py-0.5 text-xs text-secondary-900">
                {categories.find((c) => c.id === activeCat)?.name}
              </span>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <ArticleCard key={item.slug} article={item} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                没有找到匹配的档案
              </h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                试试换个关键词，或点上面的「全部圈子」恢复完整列表。你也可以通过页脚邮箱告诉我们你关心的方向。
              </p>
            </div>
          )}

          <p className="mt-10 border-t border-background-200 pt-6 text-xs leading-relaxed text-foreground-500">
            {site.disclaimer}
          </p>
        </div>
      </main>
      <SiteFooter />
      <ScrollTopButton />
    </div>
  );
}